import { useState, useEffect, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { Button } from './ui/button'
import { ArrowLeft, Download, Plus, PlusCircle, RefreshCw, Send, Sparkles, Trash2 } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table'
import { Checkbox } from './ui/checkbox'
import { apiDeleteSupplierData, apiDownloadCampaignCsv, apiDownloadCampaignExcel, apiGetCampaignDashboard, CampaignDashboardData, DashboardSupplier } from '@/lib/api'
import { SupplierDetailView } from './SupplierDetailView'
import { BatchReviewView } from './BatchReviewView'
import { CampaignSupplierDialog } from './CampaignSupplierDialog'
import { cn } from '@/lib/utils'
import { SupplierStatusBadge } from './SupplierStatusBadge'
import { CampaignStatusCard } from './CampaignStatusCard'
import { RemindModal } from './RemindModal'
import { useAuth } from '@/contexts/AuthContext'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'

type Props = {
  campaignId: string
  campaignName: string
  onBack: () => void
  // Story 3.6 — deep-link depuis le profil fournisseur.
  // Quand fourni, auto-sélectionne ce fournisseur et bascule direct sur
  // `<SupplierDetailView>` (skip la liste dashboard intermédiaire).
  initialSupplierId?: string | null
  // Quand fourni, le bouton « Retour » de `SupplierDetailView` revient au
  // profil fournisseur d'origine. Appliqué uniquement au fournisseur du
  // deep-link (initialSupplierId) — un click sur un autre fournisseur du
  // dashboard conserve le comportement classique (retour à la liste dashboard).
  onBackToSupplierProfile?: () => void
}

function formatDate(isoString: string | null): string {
  if (!isoString) return '—'
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(isoString))
}

function DashboardTableSkeleton() {
  return (
    <>
      {[1, 2, 3].map(i => (
        <TableRow key={i}>
          <TableCell className="w-10">
            <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
          </TableCell>
          <TableCell>
            <div className="space-y-1">
              <div className="h-4 bg-gray-200 rounded animate-pulse w-40" />
              <div className="h-3 bg-gray-200 rounded animate-pulse w-32" />
            </div>
          </TableCell>
          <TableCell>
            <div className="h-5 bg-gray-200 rounded-full animate-pulse w-20" />
          </TableCell>
          <TableCell>
            <div className="h-4 bg-gray-200 rounded animate-pulse w-28" />
          </TableCell>
        </TableRow>
      ))}
    </>
  )
}

export function CampaignDashboard({
  campaignId,
  campaignName,
  onBack,
  initialSupplierId = null,
  onBackToSupplierProfile,
}: Props) {
  const { user } = useAuth()
  const [data, setData] = useState<CampaignDashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [selectedSupplier, setSelectedSupplier] = useState<DashboardSupplier | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showRemindModal, setShowRemindModal] = useState(false)
  const [showBatchReview, setShowBatchReview] = useState(false)
  const [supplierToDelete, setSupplierToDelete] = useState<DashboardSupplier | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportingExcel, setExportingExcel] = useState(false)
  // Story 3.8 — modale d'ajout de fournisseur (visible si campagne DRAFT ou ACTIVE).
  const [showAddSupplierDialog, setShowAddSupplierDialog] = useState(false)

  const loadData = useCallback(() => {
    setLoading(true)
    setError(false)
    apiGetCampaignDashboard(campaignId)
      .then(setData)
      .catch(() => {
        setError(true)
        toast.error('Erreur lors du chargement du tableau de bord', { duration: Infinity })
      })
      .finally(() => setLoading(false))
  }, [campaignId])

  const handleExportCsv = useCallback(async () => {
    setExporting(true)
    try {
      const { blob, filename, isEmpty } = await apiDownloadCampaignCsv(campaignId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a) // requis pour Firefox
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      if (isEmpty) toast.warning('Aucune soumission à exporter pour le moment')
    } catch {
      toast.error("Erreur lors de l'export CSV", { duration: Infinity })
    } finally {
      setExporting(false)
    }
  }, [campaignId])

  const handleExportExcel = useCallback(async () => {
    setExportingExcel(true)
    try {
      const { blob, filename, isEmpty } = await apiDownloadCampaignExcel(campaignId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a) // requis pour Firefox
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      if (isEmpty) toast.warning('Aucune soumission à exporter pour le moment')
    } catch {
      toast.error("Erreur lors de l'export Excel", { duration: Infinity })
    } finally {
      setExportingExcel(false)
    }
  }, [campaignId])

  const handleDeleteSupplierData = useCallback(async (supplier: DashboardSupplier) => {
    setDeleting(true)
    try {
      await apiDeleteSupplierData(campaignId, supplier.id)
      toast.success('Données fournisseur supprimées')
      setSupplierToDelete(null)
      loadData()
    } catch {
      toast.error('Erreur lors de la suppression des données', { duration: Infinity })
    } finally {
      setDeleting(false)
    }
  }, [campaignId, loadData])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Story 3.6 — Auto-sélection du fournisseur deep-linké.
  // Garde anti-double-consommation : ne s'exécute qu'une fois par mount,
  // même si data change (refresh, exports). Si le supplier n'existe pas/plus
  // dans la campagne, on signale et on retombe sur la liste dashboard.
  const consumedInitialSupplierRef = useRef(false)
  // P-2 — suivi de si l'utilisateur a quitté manuellement le deep-link.
  // Après un onBack depuis SupplierDetailView vers la liste, ce ref passe à true
  // pour que isDeepLinkedSupplier ne se ré-active pas si l'utilisateur re-clique
  // le même fournisseur depuis la liste (navigation manuelle ≠ deep-link).
  const deepLinkNavigatedAwayRef = useRef(false)

  useEffect(() => {
    if (!initialSupplierId || consumedInitialSupplierRef.current) return
    if (!data) return
    consumedInitialSupplierRef.current = true
    const found = data.suppliers.find((s) => s.id === initialSupplierId)
    if (!found) {
      toast.error(
        "Ce fournisseur n'a pas/plus de soumission dans cette campagne.",
        { duration: 6000 }
      )
      return
    }
    setSelectedSupplier(found)
  }, [data, initialSupplierId])

  // P-1 — reset des gardes quand initialSupplierId redevient null (ex. onBack
  // de CampaignDashboard remonte dans Campaigns qui reset initialSupplierId).
  useEffect(() => {
    if (!initialSupplierId) {
      consumedInitialSupplierRef.current = false
      deepLinkNavigatedAwayRef.current = false
    }
  }, [initialSupplierId])

  if (showBatchReview) {
    return (
      <BatchReviewView
        campaignId={campaignId}
        campaignName={campaignName}
        onBack={() => setShowBatchReview(false)}
      />
    )
  }

  if (selectedSupplier) {
    // Story 3.6 — le retour contextuel vers le profil fournisseur n'est
    // proposé que pour le supplier deep-linké. Si l'utilisateur navigue
    // vers un autre fournisseur du dashboard, on garde le comportement
    // classique (retour à la liste dashboard).
    const isDeepLinkedSupplier =
      initialSupplierId !== null &&
      selectedSupplier.id === initialSupplierId &&
      !deepLinkNavigatedAwayRef.current
    return (
      <SupplierDetailView
        campaignId={campaignId}
        campaignName={campaignName}
        supplierId={selectedSupplier.id}
        supplierName={selectedSupplier.name}
        onBack={() => {
          deepLinkNavigatedAwayRef.current = true
          setSelectedSupplier(null)
        }}
        onBackToProfile={
          isDeepLinkedSupplier ? onBackToSupplierProfile : undefined
        }
      />
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Tableau de bord</h1>
            <p className="text-muted-foreground">{campaignName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <Button
              size="sm"
              onClick={() => setShowRemindModal(true)}
              disabled={loading}
            >
              <Send className="mr-2 h-4 w-4" />
              Relancer ({selectedIds.size})
            </Button>
          )}
          {/* Story 3.8 — ajout fournisseur sur campagne DRAFT ou ACTIVE.
              Sur ACTIVE : invitation envoyée immédiatement (cf. bandeau modale). */}
          {data && (data.campaign.status === 'DRAFT' || data.campaign.status === 'ACTIVE') && (
            <Button
              size="sm"
              onClick={() => setShowAddSupplierDialog(true)}
              disabled={loading}
            >
              <Plus className="mr-2 h-4 w-4" />
              Ajouter un fournisseur
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowBatchReview(true)}
            disabled={loading}
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Valider les extractions IA
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void handleExportCsv()}
            disabled={loading || exporting}
          >
            <Download className="mr-2 h-4 w-4" />
            {exporting ? 'Export...' : 'Exporter CSV'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void handleExportExcel()}
            disabled={loading || exportingExcel}
          >
            <Download className="mr-2 h-4 w-4" />
            {exportingExcel ? 'Export...' : 'Exporter Excel'}
          </Button>
          <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
            <RefreshCw className={cn('mr-2 h-4 w-4', loading && 'animate-spin')} />
            Rafraîchir
          </Button>
        </div>
      </div>

      <CampaignStatusCard
        stats={data?.stats ?? { total: 0, submitted: 0, inProgress: 0, notStarted: 0 }}
        loading={loading}
      />

      <TooltipProvider delayDuration={150}>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={data ? selectedIds.size === data.suppliers.length && data.suppliers.length > 0 : false}
                  onCheckedChange={(checked) => {
                    if (checked && data) setSelectedIds(new Set(data.suppliers.map(s => s.id)))
                    else setSelectedIds(new Set())
                  }}
                  aria-label="Sélectionner tous les fournisseurs"
                />
              </TableHead>
              <TableHead>Fournisseur</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Dernière action</TableHead>
              {user?.role === 'ADMIN' && <TableHead className="w-10" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <DashboardTableSkeleton />
            ) : error ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-destructive">
                  Impossible de charger les données. Vérifiez votre connexion et{' '}
                  <button className="underline" onClick={loadData}>réessayez</button>.
                </TableCell>
              </TableRow>
            ) : !data || data.suppliers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  Aucun fournisseur assigné à cette campagne.
                </TableCell>
              </TableRow>
            ) : (
              data.suppliers.map(supplier => (
                <TableRow
                  key={supplier.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setSelectedSupplier(supplier)}
                >
                  <TableCell className="w-10" onClick={e => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.has(supplier.id)}
                      onCheckedChange={(checked) => {
                        const next = new Set(selectedIds)
                        if (checked) next.add(supplier.id)
                        else next.delete(supplier.id)
                        setSelectedIds(next)
                      }}
                      aria-label={`Sélectionner ${supplier.name}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium">{supplier.name}</p>
                        {/* Story 3.8 — badge "Ajouté en cours de campagne" pour
                            les fournisseurs ajoutés après le lancement (post-launch). */}
                        {supplier.addedPostLaunch && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className="inline-flex items-center gap-1 text-xs font-medium bg-violet-100 text-violet-700 rounded px-1.5 py-0.5"
                              >
                                <PlusCircle className="h-3 w-3" aria-hidden />
                                Ajouté en cours de campagne
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              Ajouté le {formatDate(supplier.assignedAt)}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{supplier.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 flex-wrap">
                      <SupplierStatusBadge status={supplier.uxStatus} />
                      {supplier.remindedAt && (
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700">
                          Relancé
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(supplier.lastActionAt)}
                  </TableCell>
                  {user?.role === 'ADMIN' && (
                    <TableCell className="w-10" onClick={e => e.stopPropagation()}>
                      {!supplier.deletedAt && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => setSupplierToDelete(supplier)}
                          aria-label={`Supprimer les données de ${supplier.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      </TooltipProvider>

      {showRemindModal && data && (
        <RemindModal
          campaignId={campaignId}
          campaignName={campaignName}
          suppliers={data.suppliers.filter(s => selectedIds.has(s.id))}
          memberName={user?.name ?? user?.email ?? 'Vous'}
          memberEmail={user?.email ?? ''}
          onSuccess={() => {
            setShowRemindModal(false)
            setSelectedIds(new Set())
            loadData()
          }}
          onClose={() => setShowRemindModal(false)}
        />
      )}

      {/* Story 3.8 — modale d'ajout fournisseur (DRAFT + ACTIVE). Rechargement
          du dashboard à la fermeture pour refléter les nouveaux fournisseurs. */}
      {data && showAddSupplierDialog && (
        <CampaignSupplierDialog
          campaignId={campaignId}
          campaignName={campaignName}
          campaignStatus={data.campaign.status}
          open={showAddSupplierDialog}
          onClose={(changed) => {
            setShowAddSupplierDialog(false)
            if (changed) loadData()
          }}
        />
      )}

      <AlertDialog open={!!supplierToDelete} onOpenChange={(open) => { if (!open) setSupplierToDelete(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Supprimer définitivement les données de {supplierToDelete?.name}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Les données saisies et les extractions IA associées seront effacées.
              Le fournisseur sera anonymisé dans la liste.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              onClick={() => supplierToDelete && void handleDeleteSupplierData(supplierToDelete)}
            >
              {deleting ? 'Suppression...' : 'Supprimer définitivement'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
